import React from "react";
import PageHero from "../components/PageHero";
import { Award, HeartHandshake, Microscope, Users } from "lucide-react";
import { HOSPITAL } from "../data/site";

const PILLARS = [
  { icon: HeartHandshake, title: "Empathy first", text: "We treat every patient like family — listening, explaining, and supporting through every step." },
  { icon: Microscope, title: "Modern medicine", text: "Evidence-based protocols, NABL-style lab partners and contemporary equipment for accurate diagnosis." },
  { icon: Users, title: "Team you trust", text: "Senior consultants, trained nurses and dedicated paramedics who stay through your treatment." },
  { icon: Award, title: "Honest practice", text: "No unnecessary tests, no surprise bills. Clear estimates and ethical clinical decisions, always." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About us"
        title={<>A neighbourhood hospital, <span className="italic text-brand-maroon">built around families.</span></>}
        subtitle="Family HealthCare Hospital was founded with a simple promise — to bring affordable, high-quality medical care to the heart of Garhi Harsaru, Gurugram."
        image="https://images.unsplash.com/photo-1719934398679-d764c1410770?w=1400&q=85"
      />

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <h2 className="font-serif-display text-3xl md:text-4xl text-brand-ink leading-[1.1]">
            Our story
          </h2>
          <div className="mt-5 space-y-5 text-brand-ink/70 leading-relaxed">
            <p>
              In a busy market town like Garhi Harsaru, families often had to travel long distances for reliable
              hospital care. We started Family HealthCare to change that — a modern, full-service hospital right where
              you live, with the warmth of a neighbourhood clinic.
            </p>
            <p>
              From routine check-ups to advanced surgery, our consultants, ICU intensivists, ER physicians and nursing
              team work together to keep your treatment timely, transparent and dignified.
            </p>
            <p>
              We are empaneled with leading TPAs to make hospitalization cashless wherever possible, and we publish our
              rates upfront — because peace of mind is part of healing.
            </p>
          </div>
        </div>

        <aside className="md:col-span-5">
          <div className="rounded-3xl bg-brand-beige p-8 border border-brand-beige">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-maroon">By the numbers</div>
            <dl className="mt-6 space-y-5">
              <div className="flex items-baseline justify-between border-b border-brand-ink/10 pb-4">
                <dt className="text-brand-ink/65">Years of service</dt>
                <dd className="font-serif-display text-2xl text-brand-ink">10+</dd>
              </div>
              <div className="flex items-baseline justify-between border-b border-brand-ink/10 pb-4">
                <dt className="text-brand-ink/65">Specialist consultants</dt>
                <dd className="font-serif-display text-2xl text-brand-ink">15+</dd>
              </div>
              <div className="flex items-baseline justify-between border-b border-brand-ink/10 pb-4">
                <dt className="text-brand-ink/65">Beds incl. ICU</dt>
                <dd className="font-serif-display text-2xl text-brand-ink">20</dd>
              </div>
              <div className="flex items-baseline justify-between">
                <dt className="text-brand-ink/65">Emergency cover</dt>
                <dd className="font-serif-display text-2xl text-brand-ink">24×7</dd>
              </div>
            </dl>
            <a
              href={`tel:${HOSPITAL.phoneDial}`}
              data-testid="about-call-btn"
              className="mt-7 inline-flex items-center justify-center w-full rounded-full bg-brand-maroon text-white font-semibold py-3 hover:bg-brand-maroonDark transition"
            >
              {HOSPITAL.phone}
            </a>
          </div>
        </aside>
      </section>

      <section className="bg-brand-beige/60 border-y border-brand-beige py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <h2 className="font-serif-display text-3xl md:text-4xl text-brand-ink leading-[1.1] max-w-2xl">
            What we stand for.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                data-testid={`about-pillar-${title.replace(/\s+/g, "-").toLowerCase()}`}
                className="rounded-2xl bg-white p-7 border border-brand-beige"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-maroon/10 text-brand-maroon flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <h3 className="font-serif-display text-xl text-brand-ink mt-5">{title}</h3>
                <p className="text-sm text-brand-ink/65 mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
